/**
 * Views/HouseTreasury/components/common.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Shared presentation pieces for the House Treasury tabs: KPI cards, the date +
 * Game Owner filter bar, a server-side data table, status chips and the export
 * helper.
 *
 * Every table in this module pages, sorts and filters on the server (§69) — the
 * frontend never holds a full transaction history, so these components take a
 * page of rows plus a pagination descriptor and never sort or slice locally.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import * as React from "react";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Button,
  Alert,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import Paper from "@mui/material/Paper";
import CustomPagination from "../../../Components/CustomPagination";

export const GOLD = "#FFC107";
export const PANEL = "#1C1C1E";
export const BORDER = "rgba(255, 239, 153, 0.20)";

// ═════════════════════════════════════════════════════════════════════════════
// Formatting
// ═════════════════════════════════════════════════════════════════════════════

export const fmtVC = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const fmtInt = (n) => Number(n || 0).toLocaleString("en-US");

export const fmtPct = (n) => `${Number(n || 0).toFixed(2)}%`;

export const fmtDateTime = (d) => {
  if (!d) return "-";
  const date = d instanceof Date ? d : new Date(d.iso || d);
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** Transaction types are stored as UPPER_SNAKE; render them as words. */
export const humanise = (s) =>
  String(s || "")
    .replace(/_/g, " ")
    .replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase());

// ═════════════════════════════════════════════════════════════════════════════
// KPI card
// ═════════════════════════════════════════════════════════════════════════════

export const KpiCard = ({ label, value, suffix, hint, accent, dense }) => (
  <Card
    sx={{
      backgroundColor: `${PANEL} !important`,
      border: `1px solid ${BORDER}`,
      borderRadius: 2,
      padding: dense ? "10px 14px" : "14px 18px",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography sx={{ color: "#9E9E9E", fontSize: "0.7rem", letterSpacing: 0.4 }}>
        {label}
      </Typography>
      {hint ? (
        <Tooltip title={hint} arrow>
          <Box
            component="span"
            sx={{
              color: "#666",
              fontSize: "0.65rem",
              border: "1px solid #555",
              borderRadius: "50%",
              width: 13,
              height: 13,
              lineHeight: "12px",
              textAlign: "center",
              cursor: "help",
            }}
          >
            ?
          </Box>
        </Tooltip>
      ) : null}
    </Box>
    <Typography
      sx={{
        color: accent || "#FFF",
        fontSize: dense ? "1.05rem" : "1.35rem",
        fontWeight: 700,
        mt: 0.5,
        lineHeight: 1.2,
      }}
    >
      {value}
      {suffix ? (
        <Typography component="span" sx={{ fontSize: "0.7rem", color: "#9E9E9E", ml: 0.5 }}>
          {suffix}
        </Typography>
      ) : null}
    </Typography>
  </Card>
);

// ═════════════════════════════════════════════════════════════════════════════
// Status chip
// ═════════════════════════════════════════════════════════════════════════════

const STATUS_COLOURS = {
  ACTIVE: ["#1B5E20", "#A5D6A7"],
  FROZEN: ["#B71C1C", "#EF9A9A"],
  COMPLETED: ["#1B5E20", "#A5D6A7"],
  FAILED: ["#B71C1C", "#EF9A9A"],
  REVERSED: ["#E65100", "#FFCC80"],
  PENDING: ["#37474F", "#B0BEC5"],
  Generated: ["#1B5E20", "#A5D6A7"],
  Sent: ["#1B5E20", "#A5D6A7"],
  Open: ["#B71C1C", "#EF9A9A"],
  Resolved: ["#1B5E20", "#A5D6A7"],
  Ignored: ["#37474F", "#B0BEC5"],
  CRITICAL: ["#B71C1C", "#EF9A9A"],
  WARNING: ["#E65100", "#FFCC80"],
  INFO: ["#37474F", "#B0BEC5"],
};

export const StatusChip = ({ value, size = "small" }) => {
  const [bg, fg] = STATUS_COLOURS[value] || ["#333", "#DDD"];
  return (
    <Chip
      size={size}
      label={value || "-"}
      sx={{ backgroundColor: bg, color: fg, fontWeight: 600, fontSize: "0.68rem" }}
    />
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Filter bar
// ═════════════════════════════════════════════════════════════════════════════

const inputSx = {
  "& .MuiOutlinedInput-root": { color: "#FFF" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
  "& .MuiInputLabel-root": { color: "#9E9E9E" },
  "& .MuiSvgIcon-root": { color: GOLD },
  minWidth: 150,
};

export const DATE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 Days" },
  { value: "last30", label: "Last 30 Days" },
  // Sends no date bounds at all, so the server returns the complete history
  // rather than a window of it.
  { value: "alltime", label: "All Time" },
  { value: "custom", label: "Custom Range" },
];

/**
 * Date range filter. Custom bounds only render once "Custom Range" is chosen,
 * so the common case stays a single dropdown.
 *
 * The Game Owner dropdown that used to sit here is gone. Every tab now reports
 * across the whole platform, and the Complete Ledger's account search covers
 * the "one account at a time" case at every hierarchy level rather than only at
 * the top.
 */
export const TreasuryFilters = ({ filters, onChange, extra }) => {
  const set = (patch) => onChange({ ...filters, ...patch });

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        flexWrap: "wrap",
        alignItems: "center",
        mb: 2,
      }}
    >
      <FormControl size="small" sx={inputSx}>
        <InputLabel>Date Range</InputLabel>
        <Select
          label="Date Range"
          value={filters.preset || "last7"}
          onChange={(e) => set({ preset: e.target.value })}
        >
          {DATE_PRESETS.map((p) => (
            <MenuItem key={p.value} value={p.value}>
              {p.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {filters.preset === "custom" && (
        <>
          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={filters.startDate || ""}
            onChange={(e) => set({ startDate: e.target.value })}
            sx={inputSx}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={filters.endDate || ""}
            onChange={(e) => set({ endDate: e.target.value })}
            sx={inputSx}
          />
        </>
      )}

      {extra}
    </Box>
  );
};

export const filterInputSx = inputSx;

// ═════════════════════════════════════════════════════════════════════════════
// Server-side data table
// ═════════════════════════════════════════════════════════════════════════════

/**
 * @param {Array}  columns   [{ key, label, align, sortable, render, width }]
 * @param {Array}  rows
 * @param {Object} pagination { page, limit, total }
 */
export const DataTable = ({
  columns,
  rows,
  loading,
  pagination,
  onPageChange,
  onLimitChange,
  sortField,
  sortOrder,
  onSortChange,
  onRowClick,
  emptyMessage = "No records found.",
  dense,
  flat,
}) => (
  <>
    {/* No maxHeight and no stickyHeader: the table grows to fit its rows and the
        page itself scrolls, matching every other list screen in the portal.
        overflowX stays auto only so a wide table (the ledger has ten columns)
        cannot push the page sideways. */}
    <TableContainer
      component={Paper}
      sx={{
        backgroundColor: PANEL,
        // `flat` is for tables already nested inside a <Panel>, so the borders
        // do not double up.
        border: flat ? "none" : `1px solid ${BORDER}`,
        borderRadius: "8px",
        boxShadow: "none",
        width: "100%",
        maxWidth: "100%",
        // Safety net only. With wrapping headers and tight padding the tables
        // fit, but a very narrow window must scroll the table inside its own
        // panel rather than clip data or stretch the page.
        overflowX: "auto",
        "& .MuiTableCell-root": {
          color: "#FFF",
          borderBottom: "1px solid #333",
          padding: dense ? "8px 10px" : "12px 14px",
          fontSize: "0.76rem",
        },
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col.key}
                align={col.align || "left"}
                sx={{
                  backgroundColor: "#242426",
                  color: `${GOLD} !important`,
                  fontWeight: 700,
                  fontSize: "0.75rem !important",
                  // Deliberately NOT nowrap: a twelve-column table (Games,
                  // Game Owner Performance) would otherwise force a minimum
                  // width wider than the viewport. Letting "Active Players"
                  // and "Spin Rounds" wrap onto two lines is what makes those
                  // tables fit without any horizontal scrolling.
                  lineHeight: 1.25,
                  borderBottom: "none !important",
                  maxWidth: col.width || "none",
                }}
              >
                {col.sortable && onSortChange ? (
                  <TableSortLabel
                    active={sortField === col.key}
                    direction={sortField === col.key && sortOrder === "ASC" ? "asc" : "desc"}
                    onClick={() =>
                      onSortChange(
                        col.key,
                        sortField === col.key && sortOrder === "DESC" ? "ASC" : "DESC"
                      )
                    }
                    sx={{ color: `${GOLD} !important` }}
                  >
                    {col.label}
                  </TableSortLabel>
                ) : (
                  col.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 5 }}>
                <CircularProgress size={26} sx={{ color: GOLD }} />
              </TableCell>
            </TableRow>
          ) : !rows || rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                align="center"
                sx={{ py: 5, color: "#888 !important" }}
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, i) => (
              <TableRow
                key={row.id || row.txnId || row.accountId || row.gameId || i}
                hover
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={{
                  cursor: onRowClick ? "pointer" : "default",
                  "&:hover": { backgroundColor: "rgba(255,193,7,0.06)" },
                }}
              >
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    align={col.align || "left"}
                    sx={{
                      maxWidth: col.width || 200,
                      // Long ids wrap rather than widening the column.
                      overflowWrap: "anywhere",
                    }}
                  >
                    {col.render ? col.render(row) : row[col.key] ?? "-"}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>

    {/* Pagination sits outside the table panel, as it does on the other list
        screens, rather than flush against the table's bottom border. */}
    {pagination && pagination.total > 0 && (
      <Box sx={{ mt: 1.5 }}>
        <CustomPagination
          page={pagination.page}
          perPage={pagination.limit}
          total={pagination.total}
          setPage={onPageChange}
          setPerPage={onLimitChange}
        />
      </Box>
    )}
  </>
);

// ═════════════════════════════════════════════════════════════════════════════
// Export
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Writes an .xlsx or .csv from rows the server produced under the CURRENT
 * filters (§70) — never from the visible page, which would silently export a
 * subset of what the operator is looking at.
 */
export const exportRows = (rows, filename, format = "xlsx") => {
  if (!rows || rows.length === 0) return false;
  const sheet = XLSX.utils.json_to_sheet(rows);
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, "Data");
  XLSX.writeFile(book, `${filename}.${format === "csv" ? "csv" : "xlsx"}`, {
    bookType: format === "csv" ? "csv" : "xlsx",
  });
  return true;
};

export const ExportButton = ({ onExport, disabled, label = "Export" }) => {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  const handle = async (format) => {
    setBusy(true);
    setError("");
    try {
      await onExport(format);
    } catch (err) {
      setError(err.message || "Export failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: "inline-flex", flexDirection: "column", gap: 0.5 }}>
      <Box sx={{ display: "inline-flex", gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={busy ? <CircularProgress size={14} /> : <DownloadIcon />}
          disabled={disabled || busy}
          onClick={() => handle("xlsx")}
          sx={{ color: GOLD, borderColor: BORDER, textTransform: "none" }}
        >
          {label} Excel
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={disabled || busy}
          onClick={() => handle("csv")}
          sx={{ color: GOLD, borderColor: BORDER, textTransform: "none" }}
        >
          CSV
        </Button>
      </Box>
      {error ? (
        <Typography sx={{ color: "#EF9A9A", fontSize: "0.68rem" }}>{error}</Typography>
      ) : null}
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Misc
// ═════════════════════════════════════════════════════════════════════════════

// ═════════════════════════════════════════════════════════════════════════════
// Dialog styling
// ─────────────────────────────────────────────────────────────────────────────
// The portal's dialog convention, taken from Views/UserList/dialogs and
// Layout/CreateForm: a dark paper, white body text, a gold primary action and a
// faint-bordered secondary. MUI's default buttons render blue on the theme's
// dark dialog paper, which is what these override.
// ═════════════════════════════════════════════════════════════════════════════

/** Primary action — gold, matching the portal's Save / Delete / Confirm buttons. */
export const primaryBtnSx = {
  backgroundColor: "#FFD700",
  color: "#000",
  fontWeight: 500,
  textTransform: "capitalize",
  "&:hover": { backgroundColor: "#FFA000" },
  "&.Mui-disabled": { backgroundColor: "rgba(255,215,0,0.35)", color: "#555" },
};

/** Secondary action — outlined, faint gold border, white label. */
export const secondaryBtnSx = {
  border: "1px solid rgba(255, 239, 153, 0.20)",
  color: "#FFF",
  textTransform: "capitalize",
  "&:hover": { border: "1px solid #fff", background: "none" },
};

/** Spread onto every <Dialog> so title, body and actions read consistently. */
export const dialogSx = {
  "& .MuiDialog-paper": {
    backgroundColor: "#1C1C1E",
    backgroundImage: "none",
    border: `1px solid ${BORDER}`,
    borderRadius: "8px",
  },
  "& .MuiDialogTitle-root": {
    color: "#FFF",
    fontSize: "1rem",
    fontWeight: 700,
    borderBottom: `1px solid ${BORDER}`,
  },
  "& .MuiDialogContent-root": { color: "#DDD" },
  "& .MuiDialogContentText-root": { color: "#BDBDBD", fontSize: "0.85rem" },
  "& .MuiDialogActions-root": { borderTop: `1px solid ${BORDER}`, padding: "12px 20px" },
  "& .MuiInputBase-root": { color: "#FFF" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
  "& .MuiInputLabel-root": { color: "#9E9E9E" },
  "& .MuiFormHelperText-root": { color: "#777" },
};

export const SectionTitle = ({ children, action }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      mb: 1.5,
      mt: 1,
    }}
  >
    <Typography sx={{ color: "#FFF", fontSize: "0.95rem", fontWeight: 700 }}>
      {children}
    </Typography>
    {action}
  </Box>
);

export const Panel = ({ children, sx }) => (
  <Box
    sx={{
      border: `1px solid ${BORDER}`,
      borderRadius: 2,
      backgroundColor: PANEL,
      p: 2,
      ...sx,
    }}
  >
    {children}
  </Box>
);

export const ErrorBar = ({ error, onClose }) =>
  error ? (
    <Alert severity="error" onClose={onClose} sx={{ mb: 2 }}>
      {error}
    </Alert>
  ) : null;

export const Loading = ({ height = 200 }) => (
  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height }}>
    <CircularProgress sx={{ color: GOLD }} />
  </Box>
);

export const GridItem = ({ children, ...props }) => (
  <Grid item {...props}>
    {children}
  </Grid>
);
